import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Table,
  Badge,
  Row,
  Col,
  Button,
  Spinner,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import {
  collection,
  query,
  where,
  getDocs,
  getFirestore,
  doc,
  getDoc
} from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaBook,
  FaArrowLeft,
  FaFilePdf,
  FaFileExcel,
  FaCalculator,
  FaChartLine,
  FaReceipt,
  FaBalanceScale,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaFileInvoiceDollar
} from 'react-icons/fa';
import { BsCheckCircleFill, BsClockFill } from 'react-icons/bs';
import firebaseApp from '../../firebase/credenciales';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const db = getFirestore(firebaseApp);

const AsientosContables = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asientos, setAsientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planilla, setPlanilla] = useState(null);
  const [expandedAsientos, setExpandedAsientos] = useState(new Set());

  useEffect(() => {
    cargarDatos();
  }, [id]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      // Cargar planilla
      const planillaRef = doc(db, 'pl_planillas', id);
      const planillaDoc = await getDoc(planillaRef);

      if (planillaDoc.exists()) {
        setPlanilla(planillaDoc.data());
      }

      // Cargar asientos
      const q = query(
        collection(db, "pl_asientos_contables"),
        where("planilla_id", "==", id)
      );
      const querySnapshot = await getDocs(q);
      const asientosData = [];
      querySnapshot.forEach((doc) => {
        asientosData.push({ id: doc.id, ...doc.data() });
      });
      setAsientos(asientosData);
    } catch (error) {
      console.error("Error al cargar asientos:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatearMonto = (monto) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(monto || 0);
  };

  const formatearCuenta = (cuenta) => {
    const nivel = cuenta.length;
    return '\u00A0'.repeat((nivel - 2) * 2) + cuenta;
  };

  const obtenerDescripcionCuenta = (cuenta) => {
    const descripciones = {
      '62': 'GASTOS DE PERSONAL Y DIRECTORES',
      '40': 'TRIB., CONTRAP. Y APORTES AL SIST. DE PENSIONES Y DE SALUD POR PAGAR',
      '41': 'REMUNERACIONES Y PARTICIPACIONES POR PAGAR',
      '14': 'CUENTAS POR COBRAR AL PERSONAL, ACCIONISTAS Y DIRECTORES',
      '10': 'EFECTIVO Y EQUIVALENTES DE EFECTIVO',
      '622': 'BONIFICACIONES EXTRAORDINARIAS',
      '629': 'OTRAS REMUNERACIONES',
      '18': 'SERVICIOS Y OTROS CONTRATADOS POR ANTICIPADO'
    };
    return descripciones[cuenta] || cuenta;
  };

  const obtenerDescripcionSubcuenta = (cuenta) => {
    const descripciones = {
      '621': 'REMUNERACIONES',
      '6211': 'SUELDOS Y SALARIOS',
      '6214': 'GRATIFICACIONES',
      '6215': 'VACACIONES',
      '622': 'BONIFICACIÓN EXTRAORDINARIA (9%)',
      '6271': 'RÉGIMEN DE PRESTACIONES DE SALUD (VAC)',
      '6291': 'COMPENSACIÓN POR TIEMPO DE SERVICIOS',
      '401': 'GOBIERNO NACIONAL',
      '4017': 'IMPUESTO A LA RENTA',
      '40173': 'RENTA DE QUINTA CATEGORÍA',
      '403': 'INSTITUCIONES PÚBLICAS',
      '4031': 'SIS POR PAGAR (MICROEMPRESA)',
      '4032': 'ONP',
      '6273': 'SEGUROS (VIDA LEY)',
      '182': 'SEGUROS (AMORTIZACIÓN PÓLIZA)',
      '411': 'REMUNERACIONES POR PAGAR',
      '4111': 'SUELDOS Y SALARIOS POR PAGAR',
      '4114': 'GRATIFICACIONES POR PAGAR',
      '4115': 'VACACIONES POR PAGAR',
      '4119': 'BONIF. EXTRAORDINARIA POR PAGAR',
      '4151': 'CTS POR PAGAR',
      '417': 'ADMINISTRADORADORAS DE FONDOS DE PENSIONES',
      '104': 'CUENTAS CORRIENTES',
      '1041': 'CUENTAS CORRIENTES OPERATIVAS'
    };
    return descripciones[cuenta] || cuenta;
  };

  const toggleAsiento = (asientoId) => {
    const newExpanded = new Set(expandedAsientos);
    if (newExpanded.has(asientoId)) {
      newExpanded.delete(asientoId);
    } else {
      newExpanded.add(asientoId);
    }
    setExpandedAsientos(newExpanded);
  };

  const exportarPDF = () => {
    const docPDF = new jsPDF();

    docPDF.setFontSize(16);
    docPDF.text('ASIENTOS CONTABLES', 105, 20, { align: 'center' });

    if (planilla) {
      docPDF.setFontSize(12);
      docPDF.text(`PERIODO: ${planilla.periodo}`, 105, 30, { align: 'center' });
    }

    let yPosition = 40;

    asientos.forEach((asiento, index) => {
      if (index > 0) {
        docPDF.addPage();
        yPosition = 20;
      }

      docPDF.setFontSize(14);
      docPDF.text(asiento.glosa, 20, yPosition);
      yPosition += 10;

      docPDF.setFontSize(10);
      docPDF.text(`Fecha: ${new Date(asiento.fecha?.toDate()).toLocaleDateString()}`, 20, yPosition);
      yPosition += 10;

      const tableData = [];

      asiento.debe.forEach(item => {
        tableData.push([
          formatearCuenta(item.cuenta),
          item.descripcion_cuenta || obtenerDescripcionSubcuenta(item.cuenta),
          formatearMonto(item.monto),
          '-'
        ]);
      });

      asiento.haber.forEach(item => {
        tableData.push([
          formatearCuenta(item.cuenta),
          item.descripcion_cuenta || obtenerDescripcionSubcuenta(item.cuenta),
          '-',
          formatearMonto(item.monto)
        ]);
      });

      tableData.push(['TOTALES', '', formatearMonto(asiento.total_debe), formatearMonto(asiento.total_haber)]);

      autoTable(docPDF, {
        startY: yPosition,
        head: [['Cuenta', 'Descripción', 'Debe', 'Haber']],
        body: tableData,
        theme: 'striped',
        styles: {
          fontSize: 8,
          halign: 'left',
        },
        headStyles: {
          fillColor: [139, 69, 19],
          textColor: 255,
          fontStyle: 'bold',
        },
        columnStyles: {
          2: { halign: 'right' },
          3: { halign: 'right' },
        },
      });

      yPosition = docPDF.lastAutoTable.finalY + 10;
    });

    docPDF.save(`asientos_contables_${planilla?.periodo || 'general'}.pdf`);
  };

  const exportarExcel = () => {
    const workbook = XLSX.utils.book_new();

    asientos.forEach((asiento, index) => {
      const worksheetData = [
        ['Glosa', asiento.glosa],
        ['Fecha', new Date(asiento.fecha?.toDate()).toLocaleDateString()],
        [''],
        ['Cuenta', 'Descripción', 'Debe', 'Haber']
      ];

      asiento.debe.forEach(item => {
        worksheetData.push([
          item.cuenta,
          item.descripcion_cuenta || obtenerDescripcionSubcuenta(item.cuenta),
          item.monto,
          0
        ]);
      });

      asiento.haber.forEach(item => {
        worksheetData.push([
          item.cuenta,
          item.descripcion_cuenta || obtenerDescripcionSubcuenta(item.cuenta),
          0,
          item.monto
        ]);
      });

      worksheetData.push(['TOTALES', '', asiento.total_debe, asiento.total_haber]);

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, `Asiento ${index + 1}`);
    });

    XLSX.writeFile(workbook, `asientos_contables_${planilla?.periodo || 'general'}.xlsx`);
  };

  // Calcular estadísticas
  const calcularEstadisticas = () => {
    const totalDebe = asientos.reduce((sum, asiento) => sum + (asiento.total_debe || 0), 0);
    const totalHaber = asientos.reduce((sum, asiento) => sum + (asiento.total_haber || 0), 0);
    const asientosRegistrados = asientos.filter(a => a.estado === 'REGISTRADO').length;

    // Calcular Costo Empresa (Suma de cuentas de clase 6 en el Debe)
    const totalCostoEmpresa = asientos.reduce((sum, asiento) => {
      const costoAsiento = asiento.debe.reduce((s, linea) => {
        if (linea.cuenta.toString().startsWith('6')) {
          return s + (linea.monto || 0);
        }
        return s;
      }, 0);
      return sum + costoAsiento;
    }, 0);

    return {
      totalAsientos: asientos.length,
      totalDebe,
      totalHaber,
      asientosRegistrados,
      diferencia: Math.abs(totalDebe - totalHaber),
      totalCostoEmpresa
    };
  };

  const renderizarAsiento = (asiento) => {
    const isExpanded = expandedAsientos.has(asiento.id);

    const agruparCuentas = (items) => {
      return items.reduce((acc, item) => {
        const cuentaPrincipal = item.cuenta.substring(0, 2);

        if (!acc[cuentaPrincipal]) {
          acc[cuentaPrincipal] = {
            descripcion: obtenerDescripcionCuenta(cuentaPrincipal),
            items: []
          };
        }

        acc[cuentaPrincipal].items.push({
          ...item,
          descripcion_cuenta: item.descripcion_cuenta || item.descripcion || obtenerDescripcionSubcuenta(item.cuenta)
        });

        return acc;
      }, {});
    };

    const debeAgrupado = agruparCuentas(asiento.debe);
    const haberAgrupado = agruparCuentas(asiento.haber);

    return (
      <Card
        className="mb-4 border-0 shadow-sm"
        style={{ borderRadius: "15px", overflow: "hidden" }}
      >
        <Card.Header
          className="text-white py-3 cursor-pointer"
          style={{
            background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
            cursor: "pointer"
          }}
          onClick={() => toggleAsiento(asiento.id)}
        >
          <Row className="align-items-center">
            <Col>
              <div className="d-flex align-items-center">
                <FaReceipt className="me-2" />
                <div>
                  <h6 className="mb-0 fw-semibold">{asiento.glosa}</h6>
                  <small className="opacity-75">
                    <FaCalendarAlt className="me-1" size="12px" />
                    Fecha: {new Date(asiento.fecha?.toDate()).toLocaleDateString()}
                  </small>
                </div>
              </div>
            </Col>
            <Col xs="auto">
              <div className="d-flex align-items-center gap-3">
                <div className="text-center">
                  <div className="fw-bold">{formatearMonto(asiento.total_debe)}</div>
                  <small className="opacity-75">Debe</small>
                </div>
                <div className="text-center">
                  <div className="fw-bold">{formatearMonto(asiento.total_haber)}</div>
                  <small className="opacity-75">Haber</small>
                </div>
                <Badge
                  bg={asiento.estado === 'REGISTRADO' ? 'success' : 'warning'}
                  className="px-3 py-2"
                  style={{ fontSize: "0.8rem" }}
                >
                  {asiento.estado === 'REGISTRADO' ? (
                    <><BsCheckCircleFill className="me-1" size="12px" />REGISTRADO</>
                  ) : (
                    <><BsClockFill className="me-1" size="12px" />PENDIENTE</>
                  )}
                </Badge>
                <Button
                  variant="light"
                  size="sm"
                  style={{
                    borderRadius: "8px",
                    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s ease"
                  }}
                >
                  ▼
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Header>

        {isExpanded && (
          <Card.Body className="p-0">
            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
              <Table hover responsive className="mb-0" style={{ fontSize: "0.9rem" }}>
                <thead
                  style={{
                    backgroundColor: "#f8f9fa",
                    position: "sticky",
                    top: 0,
                    zIndex: 10
                  }}
                >
                  <tr>
                    <th className="border-0 py-3 px-3" style={{ width: '20%' }}>
                      <FaCalculator className="me-1" />
                      Cuenta
                    </th>
                    <th className="border-0 py-3 px-3">Descripción</th>
                    <th className="border-0 py-3 px-3 text-end" style={{ width: '15%' }}>
                      <FaChartLine className="me-1" />
                      Debe
                    </th>
                    <th className="border-0 py-3 px-3 text-end" style={{ width: '15%' }}>
                      <FaBalanceScale className="me-1" />
                      Haber
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Renderizar DEBE */}
                  {Object.entries(debeAgrupado).map(([cuentaPrincipal, grupo]) => (
                    <React.Fragment key={`debe_${cuentaPrincipal}`}>
                      <tr
                        className="fw-bold"
                        style={{
                          backgroundColor: "#D2691E15",
                          borderLeft: "4px solid #D2691E"
                        }}
                      >
                        <td className="py-2 px-3">
                          <span
                            className="badge px-2 py-1"
                            style={{
                              background: "linear-gradient(135deg, #D2691E 0%, #CD853F 100%)",
                              color: "white",
                              fontSize: "0.75rem"
                            }}
                          >
                            {cuentaPrincipal}
                          </span>
                        </td>
                        <td colSpan="3" className="py-2 px-3" style={{ color: "#8B4513" }}>
                          {grupo.descripcion}
                        </td>
                      </tr>
                      {grupo.items.map((item, index) => (
                        <tr key={`debe_item_${index}`} style={{ borderBottom: "1px solid #f1f3f4" }}>
                          <td className="py-2 px-3" style={{ paddingLeft: '2rem' }}>
                            <code className="text-muted">{formatearCuenta(item.cuenta)}</code>
                          </td>
                          <td className="py-2 px-3">{item.descripcion_cuenta}</td>
                          <td className="py-2 px-3 text-end">
                            <span className="fw-bold text-success">{formatearMonto(item.monto)}</span>
                          </td>
                          <td className="py-2 px-3 text-end text-muted">-</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}

                  {/* Renderizar HABER */}
                  {Object.entries(haberAgrupado).map(([cuentaPrincipal, grupo]) => (
                    <React.Fragment key={`haber_${cuentaPrincipal}`}>
                      <tr
                        className="fw-bold"
                        style={{
                          backgroundColor: "#B8860B15",
                          borderLeft: "4px solid #B8860B"
                        }}
                      >
                        <td className="py-2 px-3">
                          <span
                            className="badge px-2 py-1"
                            style={{
                              background: "linear-gradient(135deg, #B8860B 0%, #DAA520 100%)",
                              color: "white",
                              fontSize: "0.75rem"
                            }}
                          >
                            {cuentaPrincipal}
                          </span>
                        </td>
                        <td colSpan="3" className="py-2 px-3" style={{ color: "#8B4513" }}>
                          {grupo.descripcion}
                        </td>
                      </tr>
                      {grupo.items.map((item, index) => (
                        <tr key={`haber_item_${index}`} style={{ borderBottom: "1px solid #f1f3f4" }}>
                          <td className="py-2 px-3" style={{ paddingLeft: '2rem' }}>
                            <code className="text-muted">{formatearCuenta(item.cuenta)}</code>
                          </td>
                          <td className="py-2 px-3">{item.descripcion_cuenta}</td>
                          <td className="py-2 px-3 text-end text-muted">-</td>
                          <td className="py-2 px-3 text-end">
                            <span className="fw-bold text-danger">{formatearMonto(item.monto)}</span>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}

                  {/* Totales */}
                  <tr
                    className="fw-bold"
                    style={{
                      background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                      color: "white"
                    }}
                  >
                    <td colSpan={2} className="py-3 px-3">
                      <FaBalanceScale className="me-2" />
                      TOTALES
                    </td>
                    <td className="py-3 px-3 text-end">{formatearMonto(asiento.total_debe)}</td>
                    <td className="py-3 px-3 text-end">{formatearMonto(asiento.total_haber)}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </Card.Body>
        )}
      </Card>
    );
  };

  const estadisticas = calcularEstadisticas();

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Indicador de carga */}
        {loading && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75"
            style={{ zIndex: 1080 }}
          >
            <div className="text-center">
              <Spinner
                animation="border"
                variant="light"
                style={{ width: "3rem", height: "3rem" }}
              />
              <p className="text-white mt-3 mb-0">
                Cargando asientos contables...
              </p>
            </div>
          </div>
        )}

        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          {/* Header Premium */}
          <Card.Header
            className="text-white py-4"
            style={{
              background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
              borderBottom: "3px solid #D2691E",
            }}
          >
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaBook className="me-3" />
                Asientos Contables
              </h2>
              <p className="mb-0 opacity-75">
                Registro contable generado de la planilla de remuneraciones
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Barra superior con acciones */}
            <Card
              className="shadow-sm border-0 mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1}>
                    <div
                      className="rounded-3 p-3 text-center"
                      style={{
                        background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                        color: "white",
                      }}
                    >
                      <FaBook size={24} />
                    </div>
                  </Col>
                  <Col md={7}>
                    <h5 className="mb-1 fw-semibold">
                      Registro de Asientos Contables
                    </h5>
                    <p className="text-muted mb-0 small">
                      Visualiza y exporta los asientos contables generados automáticamente
                    </p>
                  </Col>
                  <Col md={4} className="text-end">
                    <div className="d-flex gap-2 justify-content-end">
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Exportar a PDF</Tooltip>}
                      >
                        <Button
                          variant="outline-secondary"
                          className="d-flex align-items-center gap-2"
                          onClick={exportarPDF}
                          style={{
                            borderRadius: "10px",
                            borderColor: "#8B4513",
                            color: "#8B4513"
                          }}
                        >
                          <FaFilePdf size="16px" />
                          PDF
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Exportar a Excel</Tooltip>}
                      >
                        <Button
                          variant="outline-secondary"
                          className="d-flex align-items-center gap-2"
                          onClick={exportarExcel}
                          style={{
                            borderRadius: "10px",
                            borderColor: "#D2691E",
                            color: "#D2691E"
                          }}
                        >
                          <FaFileExcel size="16px" />
                          Excel
                        </Button>
                      </OverlayTrigger>
                      <Button
                        variant="light"
                        className="d-flex align-items-center gap-2"
                        onClick={() => navigate('/planillas')}
                        style={{
                          borderRadius: "10px",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        }}
                      >
                        <FaArrowLeft size="16px" />
                        Volver
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Información de la planilla */}
            {planilla && (
              <Card
                className="shadow-sm border-0 mb-4"
                style={{ borderRadius: "15px" }}
              >
                <Card.Header
                  className="text-white py-3"
                  style={{
                    background: "linear-gradient(135deg, #D2691E 0%, #CD853F 100%)",
                    borderRadius: "15px 15px 0 0",
                  }}
                >
                  <div className="d-flex align-items-center">
                    <FaFileInvoiceDollar className="me-2" />
                    <h5 className="mb-0 fw-semibold">Información de la Planilla</h5>
                  </div>
                </Card.Header>
                <Card.Body className="p-4">
                  <Row>
                    <Col md={3}>
                      <div className="text-center">
                        <FaCalendarAlt size={24} className="mb-2" style={{ color: "#8B4513" }} />
                        <div className="fw-bold" style={{ color: "#8B4513" }}>
                          {planilla.periodo}
                        </div>
                        <small className="text-muted">Período</small>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <FaMoneyBillWave size={24} className="mb-2 text-success" />
                        <div className="fw-bold text-success">
                          {formatearMonto(planilla.total_neto)}
                        </div>
                        <small className="text-muted">Total Neto</small>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <BsCheckCircleFill size={24} className="mb-2" style={{ color: "#8B4513" }} />
                        <div>
                          <Badge
                            style={{
                              background: planilla.estado === 'PROCESADO' ?
                                "linear-gradient(135deg, #28a745 0%, #20c997 100%)" :
                                "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)",
                              border: "none",
                              fontSize: "0.8rem"
                            }}
                            className="px-3 py-2"
                          >
                            {planilla.estado}
                          </Badge>
                        </div>
                        <small className="text-muted">Estado</small>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <FaReceipt size={24} className="mb-2" style={{ color: "#8B4513" }} />
                        <div className="fw-bold" style={{ color: "#8B4513" }}>
                          {planilla.total_empleados || 0}
                        </div>
                        <small className="text-muted">Empleados</small>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}

            {/* Estadísticas de los asientos */}
            <Row className="mb-4">
              <Col>
                <Card
                  className="border-0 shadow-sm h-100"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center"
                    style={{
                      background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaReceipt size={24} className="mb-2" />
                    <h5 className="fw-bold">{estadisticas.totalAsientos}</h5>
                    <small>Asientos</small>
                  </Card.Body>
                </Card>
              </Col>

              <Col>
                <Card
                  className="border-0 shadow-sm h-100"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center"
                    style={{
                      background: "linear-gradient(135deg, #6f42c1 0%, #007bff 100%)", // Un color diferente para destacar
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaMoneyBillWave size={24} className="mb-2" />
                    <h5 className="fw-bold">{formatearMonto(estadisticas.totalCostoEmpresa)}</h5>
                    <small>Costo Empresa (Clase 6)</small>
                  </Card.Body>
                </Card>
              </Col>

              <Col>
                <Card
                  className="border-0 shadow-sm h-100"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center"
                    style={{
                      background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaChartLine size={24} className="mb-2" />
                    <h5 className="fw-bold">{formatearMonto(estadisticas.totalDebe)}</h5>
                    <small>Total Movimientos (Debe)</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col>
                <Card
                  className="border-0 shadow-sm h-100"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center"
                    style={{
                      background: "linear-gradient(135deg, #dc3545 0%, #e74c3c 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaBalanceScale size={24} className="mb-2" />
                    <h5 className="fw-bold">{formatearMonto(estadisticas.totalHaber)}</h5>
                    <small>Total Movimientos (Haber)</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col>
                <Card
                  className="border-0 shadow-sm h-100"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center"
                    style={{
                      background: estadisticas.diferencia === 0 ?
                        "linear-gradient(135deg, #28a745 0%, #20c997 100%)" :
                        "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <BsCheckCircleFill size={24} className="mb-2" />
                    <h5 className="fw-bold">
                      {estadisticas.diferencia === 0 ? "Balanceado" : formatearMonto(estadisticas.diferencia)}
                    </h5>
                    <small>Balance</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Lista de asientos */}
            {loading ? (
              <Card
                className="border-0 shadow-sm"
                style={{ borderRadius: "15px" }}
              >
                <Card.Body className="text-center py-5">
                  <Spinner animation="border" style={{ color: "#8B4513" }} />
                  <p className="mt-3 mb-0 text-muted">Cargando asientos contables...</p>
                </Card.Body>
              </Card>
            ) : asientos.length > 0 ? (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0" style={{ color: "#8B4513" }}>
                    <FaReceipt className="me-2" />
                    Asientos Contables ({asientos.length})
                  </h5>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setExpandedAsientos(new Set(asientos.map(a => a.id)))}
                      style={{
                        borderRadius: "8px",
                        borderColor: "#8B4513",
                        color: "#8B4513"
                      }}
                    >
                      Expandir Todo
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setExpandedAsientos(new Set())}
                      style={{
                        borderRadius: "8px",
                        borderColor: "#D2691E",
                        color: "#D2691E"
                      }}
                    >
                      Contraer Todo
                    </Button>
                  </div>
                </div>
                {asientos.map((asiento) => (
                  <div key={asiento.id}>
                    {renderizarAsiento(asiento)}
                  </div>
                ))}
              </>
            ) : (
              <Card
                className="border-0 shadow-sm"
                style={{ borderRadius: "15px" }}
              >
                <Card.Body className="text-center py-5">
                  <div
                    className="bg-light rounded-circle mx-auto mb-3"
                    style={{
                      width: "80px",
                      height: "80px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FaBook size={32} className="text-muted" />
                  </div>
                  <h5 className="text-muted mb-2">No hay asientos contables disponibles</h5>
                  <p className="text-muted mb-3">
                    Los asientos contables se generan automáticamente al procesar la planilla
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => navigate('/planillas')}
                    className="d-flex align-items-center gap-2 mx-auto"
                    style={{
                      borderRadius: "10px",
                      background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                      border: "none",
                    }}
                  >
                    <FaArrowLeft size="14px" />
                    Volver a Planillas
                  </Button>
                </Card.Body>
              </Card>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default AsientosContables;